import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Recycle as Motorcycle, Menu, X } from 'lucide-react';
// Import components with names that match page headings
import { CaseManagement } from './components/cases';
import { CaseHistory } from './components/history';
import { CaseDetails } from './components/case-details';
import { DocumentGallery } from './components/DocumentGallery';
import BikeInventory from './components/bikes';
import FinancialSummaryPage from './components/financial-summary';
import { ClientData } from './components/ClientData';
import { ParticleBackground } from './components/ParticleBackground';
import { ForceClientDataRedirect } from './components/features/ForceClientDataRedirect';

// Create aliases that match URL routes
const History = CaseHistory;
const Documents = DocumentGallery;
const Bikes = BikeInventory;

// Navigation link component with active state
const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link 
      to={to}
      className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
        isActive 
          ? 'text-white bg-white/20' 
          : 'text-white/90 hover:text-white hover:bg-white/10'
      }`}
    >
      {children}
    </Link>
  );
};

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <div className="min-h-screen relative">
        <ParticleBackground />
        <nav className="relative bg-[#6563ff] p-4 shadow-xl">
          <div className="container mx-auto">
            <div className="flex items-center justify-between">
              <Link to="/case-management" className="flex items-center space-x-2 text-white/90 hover:text-white">
                <Motorcycle className="h-8 w-8 text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]" />
                <span className="text-xl font-bold tracking-wider">
                  INVOICE TRACKER
                </span>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-2">
                <NavLink to="/case-management">Client Data</NavLink>
                <NavLink to="/case-history">Case History</NavLink>
                <NavLink to="/bike-management">Bike Management</NavLink>
                <NavLink to="/financial-summary">Financial Summary</NavLink>
              </div>

              {/* Mobile Menu Button */}
              <button 
                className="md:hidden text-white p-2 rounded-lg hover:bg-white/10"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>

            {/* Mobile Navigation */}
            {mobileMenuOpen && (
              <div className="md:hidden mt-4 space-y-2 bg-white/10 rounded-lg p-2">
                <div onClick={() => setMobileMenuOpen(false)}>
                  <NavLink to="/case-management">Client Data</NavLink>
                </div>
                <div onClick={() => setMobileMenuOpen(false)}>
                  <NavLink to="/case-history">Case History</NavLink>
                </div>
                <div onClick={() => setMobileMenuOpen(false)}>
                  <NavLink to="/bike-management">Bike Management</NavLink>
                </div>
                <div onClick={() => setMobileMenuOpen(false)}>
                  <NavLink to="/financial-summary">Financial Summary</NavLink>
                </div>
              </div>
            )}
          </div>
        </nav>
        
        <div className="container mx-auto px-4 py-6 relative max-w-7xl">
          <Routes>
            <Route path="/" element={<CaseManagement />} />
            <Route path="/case-management" element={<CaseManagement />} />
            <Route path="/cases" element={<CaseManagement />} /> {/* Keep for backward compatibility */}
            <Route path="/case-history" element={<History />} />
            <Route path="/history" element={<History />} /> {/* Keep for backward compatibility */}
            <Route path="/case-management/:id" element={<CaseDetails />} />
            <Route path="/cases/:id" element={<CaseDetails />} /> {/* Keep for backward compatibility */}
            {/* Primary client data editing route - matched directly from URL */}
            <Route path="/client-data/:id" element={<ClientData />} />
            {/* Force redirect route for guaranteeing navigation */}
            <Route path="/redirect-to-client-data/:id" element={<ForceClientDataRedirect />} />
            <Route path="/bike-management" element={<Bikes />} />
            <Route path="/bikes" element={<Bikes />} /> {/* Keep for backward compatibility */}
            <Route path="/financial-summary" element={<FinancialSummaryPage />} />
            <Route path="/case-management/:id/documents" element={<Documents />} />
            <Route path="/cases/:id/documents" element={<Documents />} /> {/* Keep for backward compatibility */}
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;